/**
 * <s-bra-finder>
 * Bra size finder: the visitor enters under bust and over bust (cm) and gets band + cup
 * from the supplier bra chart rows rendered on the page (`tr[data-bra-row]`, in the
 * product size guide drawer). Each row carries data-band (INTL), data-cup (A…E/DD),
 * data-code (site value, e.g. 75E), data-under ("68-72"), data-over (cm), data-fr,
 * data-us and data-available ("true"/"false").
 *
 * Band: first band whose under bust range maximum fits the measurement.
 * Cup: within that band, the cup whose over bust value is closest (ties go to the larger cup).
 * The result is kept for the visit in sessionStorage "elixir-bra-size"
 * ({ label, detail, covers }) and announced with window event "elixir:size-saved".
 */
class BraFinder extends HTMLElement {
  #form = null;
  #result = null;

  connectedCallback() {
    this.#form = this.querySelector('form');
    this.#result = this.querySelector('[data-result]');
    this.#form?.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('submit', this.#onSubmit);
  }

  #value(name) {
    const raw = this.#form.elements[name]?.value || '';
    const value = parseFloat(String(raw).replace(',', '.'));
    return value > 0 ? value : 0;
  }

  #onSubmit = (event) => {
    event.preventDefault();
    const rows = [...document.querySelectorAll('[data-bra-row]')];
    if (!rows.length || !this.#result) return;
    rows.forEach((row) => row.removeAttribute('data-recommended'));

    const under = this.#value('under');
    const over = this.#value('over');
    if (!under || !over) {
      this.#show(`<p class="m-0">${this.dataset.msgEmpty}</p>`);
      return;
    }

    // Bands in chart order, each with its cup rows.
    const bands = [];
    rows.forEach((row) => {
      let band = bands.find((b) => b.band === row.dataset.band);
      if (!band) {
        const [min, max] = row.dataset.under.split('-').map(Number);
        band = { band: row.dataset.band, min, max, rows: [] };
        bands.push(band);
      }
      band.rows.push(row);
    });

    let outOfRange = false;
    let band = bands.find((b) => under <= b.max + 0.5);
    if (!band) {
      band = bands[bands.length - 1];
      outOfRange = true;
    }
    if (under < bands[0].min - 2) outOfRange = true;

    let best = band.rows[0];
    let bestGap = Infinity;
    band.rows.forEach((row) => {
      const gap = Math.abs(Number(row.dataset.over) - over);
      if (gap <= bestGap) {
        best = row;
        bestGap = gap;
      }
    });
    const overValues = band.rows.map((row) => Number(row.dataset.over));
    if (over < Math.min(...overValues) - 2.5 || over > Math.max(...overValues) + 2.5) outOfRange = true;

    best.setAttribute('data-recommended', '');
    const label = `${best.dataset.band} ${best.dataset.cup}`;
    const detail = `${this.dataset.msgFr} ${best.dataset.fr} ${best.dataset.cup} · ${this.dataset.msgUs} ${best.dataset.us} ${best.dataset.cup}`;

    let html = `<p class="m-0 type-overline tracking-[0.18em] text-on-surface-secondary">${this.dataset.msgResult}</p>`;
    html += `<p class="m-0 mt-1 font-heading text-3xl text-accent">${label}</p>`;
    html += `<p class="m-0 mt-1 text-sm text-on-surface">${this.dataset.msgEquivalents} ${detail}</p>`;
    if (this.dataset.hasOptions === 'true') {
      if (best.dataset.available === 'true') {
        html += `<p class="m-0 mt-3 text-sm text-on-surface">${this.dataset.msgOnProduct} <strong class="font-medium">${best.dataset.code}</strong></p>`;
      } else {
        html += `<p class="m-0 mt-3 text-sm text-on-surface">${this.dataset.msgNotOnProduct}</p>`;
      }
    }
    if (outOfRange) html += `<p class="m-0 mt-3 text-sm text-on-surface-secondary">${this.dataset.msgOut}</p>`;
    this.#show(html);

    try {
      sessionStorage.setItem('elixir-bra-size', JSON.stringify({ label, detail, covers: best.dataset.code }));
    } catch {
      return;
    }
    this.querySelectorAll('[data-after-result]').forEach((el) => { el.hidden = false; });
    window.dispatchEvent(new CustomEvent('elixir:size-saved'));
  };

  #show(html) {
    this.#result.innerHTML = html;
    this.#result.hidden = false;
  }
}

if (!customElements.get('s-bra-finder')) {
  customElements.define('s-bra-finder', BraFinder);
}
